import { useMemo } from "react";
import { Calendar, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  STATUS_LABELS,
  STATUS_COLORS,
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  formatDueDate,
  isOverdue,
  initials,
  statusLabel,
  statusColor,
} from "@/lib/taskHelpers";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

function startOfDay(d = new Date()) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export default function TaskCalendarView({ tasks, statuses, onUpdate, onOpen }) {
  const groups = useMemo(() => {
    const now = startOfDay();
    const tomorrow = startOfDay(new Date(now.getTime() + 24 * 3600 * 1000));
    const weekEnd = startOfDay(new Date(now.getTime() + 7 * 24 * 3600 * 1000));
    const out = {
      overdue: [],
      today: [],
      tomorrow: [],
      this_week: [],
      later: [],
      no_date: [],
    };
    tasks.forEach((t) => {
      if (t.status === "done") return;
      if (!t.due_date) { out.no_date.push(t); return; }
      const d = startOfDay(new Date(t.due_date));
      if (d < now) out.overdue.push(t);
      else if (d.getTime() === now.getTime()) out.today.push(t);
      else if (d.getTime() === tomorrow.getTime()) out.tomorrow.push(t);
      else if (d <= weekEnd) out.this_week.push(t);
      else out.later.push(t);
    });
    return out;
  }, [tasks]);

  const sections = [
    { key: "overdue", title: "Overdue", icon: AlertCircle, color: "#dc2626" },
    { key: "today", title: "Today", icon: Calendar, color: "#005e7a" },
    { key: "tomorrow", title: "Tomorrow", icon: Calendar, color: "#0284c7" },
    { key: "this_week", title: "This Week", icon: Calendar, color: "#16a34a" },
    { key: "later", title: "Later", icon: Calendar, color: "#82828c" },
    { key: "no_date", title: "No due date", icon: CheckCircle2, color: "#4a4a52" },
  ];

  return (
    <div className="flex flex-col gap-6" data-testid="task-calendar">
      {sections.map((s) => {
        const items = groups[s.key];
        if (items.length === 0) return null;
        const Icon = s.icon;
        return (
          <div key={s.key}>
            <div className="flex items-center gap-2 mb-3">
              <Icon className="w-4 h-4" style={{ color: s.color }} />
              <p className="bloq-headline text-lg" style={{ color: s.color }}>{s.title}</p>
              <span className="text-xs text-[#82828c]">{items.length}</span>
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {items.map((t) => <CalCard key={t.task_id} task={t} statuses={statuses} onOpen={onOpen} onUpdate={onUpdate} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CalCard({ task, statuses, onOpen, onUpdate }) {
  const prio = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
  const stat = statusColor(task.status, statuses);
  const sLabel = statusLabel(task.status, statuses) || STATUS_LABELS[task.status] || task.status;
  const overdue = task.status !== "done" && isOverdue(task.due_date);
  return (
    <div
      onClick={() => onOpen(task.task_id)}
      className="bloq-card p-4 cursor-pointer hover:-translate-y-0.5 transition-transform"
      data-testid={`cal-card-${task.task_id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-widest" style={{ background: stat.bg, color: stat.c }}>
          {sLabel}
        </span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-widest" style={{ background: prio.bg, color: prio.c }}>
          {PRIORITY_LABELS[task.priority]}
        </span>
      </div>
      <p className="font-medium text-sm leading-snug">{task.title}</p>
      <div className="flex items-center justify-between mt-2.5">
        <span className={`text-xs inline-flex items-center gap-1 ${overdue ? "text-[#dc2626] font-semibold" : "text-[#82828c]"}`}>
          <Calendar className="w-3 h-3" /> {task.due_date ? formatDueDate(task.due_date) : "No due date"}
        </span>
        {task.assignee_id && (
          <Avatar className="w-6 h-6 rounded-md" title={task.assignee_name || ""}>
            <AvatarImage src={task.assignee_avatar || ""} className="rounded-md" />
            <AvatarFallback className="bg-[#005e7a] text-white text-[9px] rounded-md">{initials(task.assignee_name)}</AvatarFallback>
          </Avatar>
        )}
      </div>
    </div>
  );
}
